import React, { FC, useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Tooltip, Colors } from '@inplayer-org/inplayer-ui';
import styled, { css } from 'styled-components';
import { GoSync } from 'react-icons/go';

// utils
import isEmpty from 'lodash/isEmpty';

// constants
import { OvpTypes } from 'pages/MerchantPages/Assets/InitialCreate/OvpVideoType';

// hooks
import useSharedBoolean from 'pages/MerchantPages/Assets/containers/EditAsset/hooks/useSharedBoolean';

// types
import { IconType } from 'react-icons';
import { Details } from 'ducks/merchantDucks/items/types';

// actions
import { pullBrightcoveDetails, syncOvpVideos } from 'ducks/merchantDucks/items/actions';
import { loadExternalAccount } from 'ducks/merchantDucks/externalAccount/actions';

// selectors
import {
  getItemDetails,
  getItemsState,
  getOvpVideoDetails,
  getTemplateDetails,
} from 'ducks/merchantDucks/items/selectors';
import { getIntegrationKeysForOvp } from 'ducks/merchantDucks/externalAccount/selectors';

// styled components
import { StyledReactIcon } from 'pages/MerchantPages/Audience/components/AudienceDetailsBox';

const StyledIcon = styled(StyledReactIcon)<{ disabled?: boolean }>`
  vertical-align: middle;
  margin-left: 0.5rem;

  :hover {
    transform: scale(1.2);
    transition: 0.2s ease;
  }

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: default;

      :hover {
        & > svg {
          cursor: default;
        }
      }
    `}
`;

interface SyncIcon {
  as: IconType;
  color: string;
  disabled: boolean;
  onClick?: () => void;
}

const ItemSync: FC = () => {
  const dispatch = useDispatch();

  const [videoId, setVideoId] = useState('');
  const [ovpType, setOvpType] = useState('');
  const [isAnalyticsView] = useSharedBoolean('isAnalyticsView');

  // selectors
  const { syncInProgress } = useSelector(getItemsState);
  const ovpVideoDetails = useSelector(getOvpVideoDetails);
  const brightcoveKeys = useSelector(getIntegrationKeysForOvp(OvpTypes.BRIGHTCOVE));

  const assetDetails = useSelector(getItemDetails);
  const templateDetails = useSelector(getTemplateDetails);

  const { pathname } = useLocation();
  const {
    item_type: { name: itemType = '' } = {},
    content = '',
    access_control_type: { id: accessControlTypeId },
  } = useMemo(() => (pathname.includes('templates') ? templateDetails : assetDetails) as Details, [
    pathname,
    assetDetails,
    templateDetails,
  ]);

  useEffect(() => {
    dispatch(loadExternalAccount(OvpTypes.BRIGHTCOVE));
    dispatch(loadExternalAccount(OvpTypes.QBRICK));
    dispatch(loadExternalAccount(OvpTypes.FLOWPLAYER));
  }, []);

  useEffect(() => {
    const { isFetchedBrightcove, isFetchingBrightcove } = ovpVideoDetails;
    // Load ovpVideoDetails in order to check if the asset is from ovp
    if (
      content !== '' &&
      !isFetchedBrightcove &&
      !isFetchingBrightcove &&
      itemType === `${OvpTypes.BRIGHTCOVE}_asset`
    ) {
      try {
        const { video_id = '' } = JSON.parse(unescape(content || '{}'));

        if (video_id !== '' && video_id !== videoId) {
          setOvpType(OvpTypes.BRIGHTCOVE);
          setVideoId(video_id);
          dispatch(pullBrightcoveDetails(OvpTypes.BRIGHTCOVE, Number(video_id)));
        }
      } catch (_) {}
    }
  }, [content, ovpVideoDetails]);

  const syncVideo = () => {
    dispatch(
      syncOvpVideos({
        ovpName: ovpType,
        videosIds: [videoId],
        syncAll: false,
        isFromAssetDetails: true,
        isUpdate: true,
        accessControlType: accessControlTypeId,
      })
    );
  };

  let syncIconProps: SyncIcon = {
    as: GoSync,
    color: syncInProgress ? Colors.green : Colors.concrete,
    disabled: syncInProgress,
  };

  if (!syncInProgress) {
    syncIconProps = {
      ...syncIconProps,
      onClick: syncVideo,
    };
  }

  if (
    itemType === `${OvpTypes.BRIGHTCOVE}_asset` &&
    !isEmpty(brightcoveKeys) &&
    ovpVideoDetails &&
    ovpVideoDetails.isFromOvp &&
    !isAnalyticsView
  ) {
    return (
      <Tooltip content="Sync with brightcove video">
        <StyledIcon {...syncIconProps} />
      </Tooltip>
    );
  }

  return null;
};

export default ItemSync;
